import React, { useEffect, useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import axios from '../axios'
import PlayerImageBadge from '../components/PlayerImageBadge'
import { Confetti, WinnersCarousel } from '../components/WinnersCarousel'



const STATUS_META = {
    active:    { label: '● LIVE',    bg: '#dc2626', color: '#fff' },
    upcoming:  { label: 'Upcoming',  bg: '#f97316', color: '#fff' },
    completed: { label: 'Completed', bg: '#1f2937', color: '#fff' },
    cancelled: { label: 'Cancelled', bg: '#6b7280', color: '#fff' },
    draft:     { label: 'Draft',     bg: '#f3f4f6', color: '#9ca3af' },
}

function StatusBadge({ status, className }) {
    const m = STATUS_META[status] || STATUS_META.draft
    return (
        <span className={className} style={{
            background: m.bg, color: m.color,
            fontSize: '0.8rem', fontWeight: 900,
            padding: '0.35rem 1rem', borderRadius: 6,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
        }}>{m.label}</span>
    )
}

function SectionTitle({ icon, children }) {
    return (
        <div style={{
            fontSize: '0.65rem', fontWeight: 800, color: 'var(--bnb-gray)',
            textTransform: 'uppercase', letterSpacing: '0.1em',
            borderBottom: '1.5px solid var(--bnb-border)',
            paddingBottom: '0.4rem', marginBottom: '0.85rem', marginTop: '1.75rem',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
        }}>
            {icon && <i className={`fa-solid ${icon}`}></i>}
            {children}
        </div>
    )
}

function InfoRow({ icon, label, value }) {
    if (!value) return null
    return (
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
            <i className={`fa-solid ${icon}`} style={{ color: 'var(--bnb-navy)', width: 16, marginTop: 2, flexShrink: 0 }}></i>
            <div>
                <span style={{ fontSize: '0.7rem', color: 'var(--bnb-gray)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{label}</span>
                <div style={{ fontSize: '0.9rem', color: '#111827', fontWeight: 600 }}>{value}</div>
            </div>
        </div>
    )
}

// ── Tournament Detail Screen ──────────────────────────────────────────────────

function TournamentDetailScreen() {
    const { id } = useParams()
    const navigate = useNavigate()
    const { userInfo } = useSelector(state => state.userLogin)

    const [tournament, setTournament] = useState(null)
    const [loading, setLoading]       = useState(true)
    const [error, setError]           = useState(null)

    const [isRegistered, setIsRegistered]         = useState(false)
    const [registrationStatus, setRegStatus]      = useState(null) // 'registered' | 'confirmed' | 'cancelled'
    const [showRegBadge, setShowRegBadge]         = useState(true)
    const [dismissingBadge, setDismissingBadge]   = useState(false)
    const [paymentStatus, setPaymentStatus]       = useState(null) // 'pending' | 'paid' | 'refunded'
    const [regLoading, setRegLoading]             = useState(false)
    const [regError, setRegError]                 = useState(null)
    const [contestHoleModal, setContestHoleModal] = useState(null)
    const [courseModal, setCourseModal]           = useState(null)
    const [modalHoleIdx, setModalHoleIdx]         = useState(0)
    const [courseTab, setCourseTab]               = useState(0)
    const [contestTab, setContestTab]             = useState(0)
    const [awardTab, setAwardTab]                 = useState(0)
    const [mainTab, setMainTab]                   = useState('about')
    const [teamSort, setTeamSort]                 = useState('total')
    const [hoveredCol, setHoveredCol]             = useState(null)
    const [liveModal, setLiveModal]               = useState(null) // 'standings' | 'scorecard'
    const [liveScorecardRound, setLiveScorecardRound] = useState(0)
    const [liveScores, setLiveScores]             = useState({}) // {`${team_id}_${day_id}_${hole}`: score}
    const [teamResults, setTeamResults]           = useState({}) // { `${teamId}_${dayId}`: { total_score, score_to_par } }

    // Fetch tournament detail
    useEffect(() => {
        setLoading(true)
        const config = userInfo?.token ? { headers: { Authorization: `Bearer ${userInfo.token}` } } : {}
        axios.get(`/api/tournaments/public/${id}/`, config)
            .then(res => { 
                setTournament(res.data)
                setLoading(false) 
            })
            .catch(err => { setError(err.response?.data?.detail || err.message); setLoading(false) })
    }, [id, userInfo])

    // Check if current user is already registered
    useEffect(() => {
        if (!userInfo) return
        axios.get(`/api/tournaments/public/${id}/register/`, {
            headers: { Authorization: `Bearer ${userInfo.token}` }
        }).then(res => {
            setIsRegistered(res.data.is_registered)
            setRegStatus(res.data.registration_status)
            setPaymentStatus(res.data.payment_status)
        }).catch(() => {})
    }, [id, userInfo])

    // Fetch team results when scorecard modal opens or round changes
    useEffect(() => {
        if (liveModal !== 'scorecard' || !tournament?.scorecards?.length) return
        
        // Use the user's actual team ID from backend
        const userTeamId = tournament.user_team_id
        if (!userTeamId) return
        
        const fetchTeamResults = async () => {
            try {
                const res = await axios.get(`/api/tournaments/public/${id}/team-results/`, {
                    params: { team_id: userTeamId },
                    headers: userInfo ? { Authorization: `Bearer ${userInfo.token}` } : {}
                })
                const resultsMap = {}
                if (res.data.results) {
                    res.data.results.forEach(r => {
                        const key = `${r.team_id}_${r.day_id}`
                        resultsMap[key] = r
                    })
                }
                setTeamResults(resultsMap)
            } catch (e) {
                // Silent fail
            }
        }
        
        fetchTeamResults()
            }, [liveModal, liveScorecardRound, tournament?.id, userInfo, id, tournament?.user_team_id, tournament?.scorecards?.length])
    // Refresh tournament data when standings modal opens to get latest team results
    useEffect(() => {
        if (liveModal !== 'standings') return
        
        const config = userInfo?.token ? { headers: { Authorization: `Bearer ${userInfo.token}` } } : {}
        axios.get(`/api/tournaments/public/${id}/`, config)
            .then(res => setTournament(res.data))
            .catch(() => {})
    }, [liveModal, id, userInfo])

    const handleRegister = () => {
        if (!userInfo) { navigate('/login'); return }
        setRegLoading(true)
        setRegError(null)
        axios.post(`/api/tournaments/public/${id}/register/`, {}, {
            headers: { Authorization: `Bearer ${userInfo.token}` }
        })
            .then(res => {
                setIsRegistered(true)
                setRegStatus(res.data.registration_status || 'registered')
                setRegLoading(false)
            })
            .catch(err => { setRegError(err.response?.data?.detail || err.message); setRegLoading(false) })
    }

    // Enrich highlights with round, course, yardage info and normalize structure for WinnersCarousel
    // Must be BEFORE early returns (React hook rule)
    const enrichedHighlights = useMemo(() => {
        if (!tournament?.highlights) return []
        
        const t = tournament
        
        // Build course lookup map
        const courseMap = {}
        if (t.days) {
            t.days.forEach(day => {
                if (day.course_id) {
                    courseMap[day.course_id] = {
                        name: day.course,
                        par: day.course_par,
                        yardage: day.course_yardage
                    }
                }
            })
        }
        

        return t.highlights.map(h => {
            const enriched = { ...h }
            // Note: kind comes from API (already set to 'contest' or 'award')
            
            // For contests: enrich with round, course, yardage
            if (enriched.kind === 'contest' && t.contests && h.contest_id) {
                const contest = t.contests.find(c => c.id === h.contest_id)
                if (contest) {
                    enriched.contest_name = contest.name
                    
                    // Find matching round for course details
                    if (contest.rounds && h.round_id) {
                        const round = contest.rounds.find(r => r.id === h.round_id)
                        if (round) {
                            enriched.round = round.round
                            enriched.course_name = courseMap[round.course_id]?.name || courseMap[contest.rounds[0]?.course_id]?.name
                            enriched.yardage = courseMap[round.course_id]?.yardage || courseMap[contest.rounds[0]?.course_id]?.yardage
                        }
                    }
                }
            }
            
            return enriched
        })
    }, [tournament])

    if (loading) return (
        <div style={{ textAlign: 'center', padding: '4rem' }}>
            <Spinner animation="border" style={{ color: 'var(--bnb-navy)' }} />
        </div>
    )

    if (error) return (
        <div style={{ maxWidth: 1200, margin: '2rem auto', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '1.25rem', color: '#991b1b' }}>
            {error}
        </div>
    )

    if (!tournament) return null

    const t = tournament
    const canRegister = (t.status === 'upcoming' || t.status === 'active')


    // Registration cutoff = first day date minus 14 days
    const firstDayDate = t.days?.length ? t.days[0].day_date : null
    const regCutoffStr = firstDayDate
        ? (() => {
            const d = new Date(firstDayDate + 'T00:00:00')
            d.setDate(d.getDate() - 14)
            return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          })()
        : null

    const formatDate = (ds) => ds
        ? new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
        : null

    const formatTime = (ts) => {
        if (!ts) return null
        const [h, m] = ts.split(':')
        const d = new Date(); d.setHours(+h, +m)
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    }

    return (
        <>
        <style>{`
            @keyframes bnb-badge-fade-out {
                from { opacity: 1; transform: translateY(0) scale(1); }
                to { opacity: 0; transform: translateY(-8px) scale(0.95); }
            }
            @keyframes bnb-pulse {
                0%, 100% { opacity: 1; transform: scale(1); }
                50% { opacity: 0.65; transform: scale(1.08); }
            }
            .bnb-view-hole {
                animation: bnb-pulse 1.5s ease-in-out infinite;
                display: inline-block;
            }
            @keyframes bnb-tab-fade {
                from { opacity: 0; transform: translateY(6px); }
                to   { opacity: 1; transform: translateY(0); }
            }
            .bnb-tab-content {
                animation: bnb-tab-fade 0.3s ease forwards;
            }
            @keyframes bnb-badge-shimmer {
                0%    { transform: translateX(-120%) skewX(-20deg); opacity: 1; }
                18%   { transform: translateX(320%) skewX(-20deg);  opacity: 1; }
                19%   { transform: translateX(320%) skewX(-20deg);  opacity: 0; }
                99%   { transform: translateX(-120%) skewX(-20deg); opacity: 0; }
                100%  { transform: translateX(-120%) skewX(-20deg); opacity: 1; }
            }
            .bnb-badge-shimmer {
                position: relative;
                overflow: hidden;
            }
            .bnb-badge-shimmer::after {
                content: '';
                position: absolute;
                top: 0; left: 0;
                width: 40%; height: 100%;
                background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.28) 50%, transparent 100%);
                animation: bnb-badge-shimmer 6s linear infinite;
            }
            .bnb-reg-deadline {
                position: absolute;
                top: 0; bottom: 0; right: 0;
                max-width: 40%;
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                justify-content: center;
                padding: 0 2.75rem;
                text-align: right;
            }
            .bnb-round-gradient {
                background: linear-gradient(to right, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 40%, transparent 100%);
                padding: 0.5rem 1rem;
                margin-left: -1rem;
                width: calc(50% + 1rem);
                display: flex;
                flex-direction: column;
                gap: 0.2rem;
            }
            @media (max-width: 768px) {
                .bnb-round-gradient {
                    width: calc(100% + 2rem);
                }
            }
            .bnb-reg-status {
                position: absolute;
                bottom: 1.1rem;
                right: 2.75rem;
                z-index: 30;
                max-width: 42%;
            }
            .bnb-reg-btn {
                padding: 0.5rem 1.4rem;
                border-radius: 7px;
                background: var(--bnb-navy);
                color: #fff;
                border: 2px solid rgba(255,255,255,0.3);
                font-weight: 800;
                font-size: 0.85rem;
                box-shadow: 0 2px 12px rgba(0,0,0,0.35);
                transition: background 0.18s ease, color 0.18s ease;
                cursor: pointer;
            }
            .bnb-reg-btn:hover:not(:disabled) {
                background: #1a4fba;
                color: #facc15;
            }
            .bnb-reg-btn:disabled {
                cursor: not-allowed;
                opacity: 0.7;
            }
            @media (max-width: 768px) {
                .bnb-reg-status {
                    max-width: 90%;
                    right: 5%;
                }
                .bnb-reg-deadline { padding: 0 1rem; }
            }
        `}</style>
        {/* Full-width banner image — outside the max-width container */}
        {t.image_url && (
            <div style={{
                margin: '-1rem -1.5rem 0',
                height: 380,
                background: '#c8c9ca',
                overflow: 'hidden',
                borderRadius: '0 0 18px 18px',
                position: 'relative',
            }}>
                <img src={t.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', mixBlendMode: 'multiply' }} />

                {/* Dark gradient so overlay text is readable */}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.52) 0%, transparent 45%)', pointerEvents: 'none', borderRadius: '0 0 18px 18px' }} />

                {/* Top-left: logo + name + badge + date + location */}
                <div style={{
                    position: 'absolute', top: 'calc(1.1rem + 20px)', left: 0,
                    background: 'linear-gradient(to right, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.72) 40%, rgba(0,0,0,0.3) 70%, transparent 100%)',
                    padding: '5px 4rem 5px 1.5rem',
                    display: 'flex', alignItems: 'flex-start', gap: '1.25rem',
                }}>
                    {t.logo_url && (
                        <div style={{ width: 108, height: 108, borderRadius: 14, overflow: 'hidden', flexShrink: 0, filter: 'drop-shadow(0 3px 10px rgba(0,0,0,0.55))' }}>
                            <img src={t.logo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', justifyContent: 'center' }}>
                        {/* Name + badge inline */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <div className="bnb-tournament-name">
                                {t.name}
                            </div>
                            <StatusBadge status={t.status} className="bnb-badge-shimmer" />
                        </div>
                        {/* Date + Location below */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                            {t.days?.length > 0 && t.days[0].day_date && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.92)', fontSize: '0.85rem', fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                    <i className="fa-solid fa-calendar" style={{ fontSize: '0.78rem', color: 'var(--bnb-gold)' }} />
                                    {formatDate(t.days[0].day_date)}
                                </div>
                            )}
                            {t.location && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: 'rgba(255,255,255,0.92)', fontSize: '0.85rem', fontWeight: 600, textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
                                    <i className="fa-solid fa-location-dot" style={{ fontSize: '0.78rem', color: 'var(--bnb-gold)' }} />
                                    {t.location}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {/* Winners carousel for completed events */}
                {(t.status === 'completed' || t.status === 'cancelled') && (
                    <div className="bnb-winners-carousel-mobile">
                        <div className="bnb-wc-confetti-layer"><Confetti /></div>
                        <WinnersCarousel winnerDetails={t.winner_details || []} highlights={enrichedHighlights} />
                    </div>
                )}

                {/* Center: Registration deadline (not yet registered) */}
                {canRegister && !isRegistered && regCutoffStr && (
                    <div className="bnb-reg-deadline">
                        <div style={{ fontSize: '1.85rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#fff', lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.7)', whiteSpace: 'nowrap' }}>
                            Registration Ends
                        </div>
                        <div style={{ fontSize: '1.65rem', fontWeight: 900, color: '#fff', lineHeight: 1.2, textShadow: '0 2px 12px rgba(0,0,0,0.75)', whiteSpace: 'nowrap' }}>
                            {regCutoffStr}
                        </div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#facc15', textShadow: '0 1px 6px rgba(0,0,0,0.8)', marginBottom: '0.75rem', whiteSpace: 'nowrap' }}>
                            {t.entry_fee > 0 ? `Entry Fee: $${parseFloat(t.entry_fee).toFixed(2)}` : 'Free to Enter'}
                        </div>
                        <button
                            onClick={handleRegister}
                            disabled={regLoading}
                            className="bnb-reg-btn"
                            style={{
                                cursor: regLoading ? 'not-allowed' : 'pointer',
                                opacity: regLoading ? 0.7 : 1,
                            }}
                        >
                            {regLoading ? 'Registering…' : userInfo ? 'Register' : 'Login to Register'}
                        </button>
                        {regError && <div style={{ fontSize: '0.7rem', color: '#fca5a5', marginTop: '0.3rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{regError}</div>}
                    </div>
                )}

                {/* Bottom-right: registration status badge */}
                {isRegistered && showRegBadge && (() => {
                    const cfg = {
                        confirmed: { icon: 'fa-circle-check', color: '#4ade80', border: 'rgba(74,222,128,0.55)', label: "You're Confirmed!" },
                        registered: { icon: 'fa-clock',         color: '#fcd34d', border: 'rgba(252,211,77,0.55)',  label: 'Awaiting Approval' },
                        cancelled:  { icon: 'fa-circle-xmark',  color: '#fca5a5', border: 'rgba(252,165,165,0.55)', label: 'Registration Cancelled' },
                    }[registrationStatus] || { icon: 'fa-clock', color: '#fcd34d', border: 'rgba(252,211,77,0.55)', label: 'Awaiting Approval' }
                    const handleDismiss = () => {
                        setDismissingBadge(true)
                        setTimeout(() => setShowRegBadge(false), 300)
                    }
                    return (
                        <div className="bnb-reg-status" style={{ animation: dismissingBadge ? 'bnb-badge-fade-out 0.3s ease forwards' : 'none', cursor: 'pointer' }} onClick={handleDismiss}>
                            <div style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px)', border: `1.5px solid ${cfg.border}`, borderRadius: 10, padding: '0.6rem 1rem', display: 'flex', alignItems: 'center', gap: '0.6rem', whiteSpace: 'nowrap' }}>
                                <i className={`fa-solid ${cfg.icon}`} style={{ fontSize: '1.1rem', color: cfg.color, flexShrink: 0 }} />
                                <span style={{ fontWeight: 800, color: '#fff', fontSize: '0.88rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{cfg.label}</span>
                            </div>
                            {paymentStatus && (
                                <div style={{ marginTop: '0.4rem', display: 'flex', justifyContent: 'flex-end' }}>
                                    <div style={{
                                        background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px)',
                                        border: `1.5px solid ${paymentStatus === 'paid' ? 'rgba(74,222,128,0.55)' : paymentStatus === 'refunded' ? 'rgba(252,165,165,0.55)' : 'rgba(252,211,77,0.55)'}`,
                                        borderRadius: 10, padding: '0.6rem 1rem',
                                        display: 'flex', alignItems: 'center', gap: '0.6rem', whiteSpace: 'nowrap',
                                    }}>
                                        <i className={`fa-solid ${paymentStatus === 'paid' ? 'fa-circle-check' : paymentStatus === 'refunded' ? 'fa-rotate-left' : 'fa-hourglass-half'}`}
                                            style={{ fontSize: '1.1rem', color: paymentStatus === 'paid' ? '#4ade80' : paymentStatus === 'refunded' ? '#fca5a5' : '#fcd34d', flexShrink: 0 }} />
                                        <span style={{ fontWeight: 800, color: '#fff', fontSize: '0.88rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>
                                            Payment: {paymentStatus === 'paid' ? 'Paid' : paymentStatus === 'refunded' ? 'Refunded' : 'Pending'}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )
                })()}
            </div>
        )}

        {/* Registration status badge fallback — shown below hero when no image */}
        {isRegistered && showRegBadge && !t.image_url && (() => {
            const cfg = {
                confirmed: { icon: 'fa-circle-check', color: '#16a34a', border: '#bbf7d0', bg: '#f0fdf4', label: "You're Confirmed!" },
                registered: { icon: 'fa-clock',         color: '#d97706', border: '#fcd34d', bg: '#fffbeb', label: 'Awaiting Approval' },
                cancelled:  { icon: 'fa-circle-xmark',  color: '#dc2626', border: '#fca5a5', bg: '#fef2f2', label: 'Registration Cancelled' },
            }[registrationStatus] || { icon: 'fa-clock', color: '#d97706', border: '#fcd34d', bg: '#fffbeb', label: 'Awaiting Approval' }
            const handleDismiss = () => {
                setDismissingBadge(true)
                setTimeout(() => setShowRegBadge(false), 300)
            }
            return (
                <div style={{ maxWidth: 1200, margin: '0.75rem auto 0', animation: dismissingBadge ? 'bnb-badge-fade-out 0.3s ease forwards' : 'none' }}>
                    <div style={{ background: cfg.bg, border: `1.5px solid ${cfg.border}`, borderRadius: 10, padding: '0.75rem 1.1rem', display: 'inline-flex', alignItems: 'center', gap: '0.65rem', cursor: 'pointer' }} onClick={handleDismiss}>
                        <i className={`fa-solid ${cfg.icon}`} style={{ fontSize: '1.2rem', color: cfg.color, flexShrink: 0 }} />
                        <span style={{ fontWeight: 800, color: cfg.color, fontSize: '0.9rem' }}>{cfg.label}</span>
                    </div>
                </div>
            )
        })()}

        <div style={{ maxWidth: 1200, margin: '0 auto', paddingTop: '1.25rem', paddingBottom: '3rem' }}>

            {/* Back link */}
            <button
                onClick={() => navigate('/events')}
                style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--bnb-navy)', fontWeight: 700, fontSize: '0.8rem',
                    padding: '0 0 1rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
                }}
            >
                <i className="fa-solid fa-arrow-left"></i> All Events
            </button>

            {/* Live action buttons — ACTIVE tournaments only */}
            {t.status === 'active' && (
                <div className="bnb-tds-active-tournament-container">
                    <button onClick={() => setMainTab('teams')} style={{
                        flex: 1, padding: '0.85rem', borderRadius: 10, cursor: 'pointer',
                        background: 'var(--bnb-navy)', color: '#fff', border: 'none',
                        fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '0.5rem',
                        boxShadow: '0 2px 12px rgba(0,48,135,0.2)',
                        transition: 'background 0.15s, transform 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background='#1a4fba'; e.currentTarget.style.transform='translateY(-1px)' }}
                    onMouseLeave={e => { e.currentTarget.style.background='var(--bnb-navy)'; e.currentTarget.style.transform='translateY(0)' }}>
                        <i className="fa-solid fa-ranking-star"></i> View Standings
                    </button>
                    <button onClick={() => setMainTab('scorecard')} style={{
                        flex: 1, padding: '0.85rem', borderRadius: 10, cursor: 'pointer',
                        background: 'var(--bnb-navy)', color: '#fff', border: 'none',
                        fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '0.5rem',
                        boxShadow: '0 2px 12px rgba(0,48,135,0.2)',
                        transition: 'background 0.15s, transform 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background='#1a4fba'; e.currentTarget.style.transform='translateY(-1px)' }}
                    onMouseLeave={e => { e.currentTarget.style.background='var(--bnb-navy)'; e.currentTarget.style.transform='translateY(0)' }}>
                        <i className="fa-solid fa-table"></i> View Event Scorecard
                    </button>
                    <button onClick={() => setLiveModal('scorecard')} style={{
                        flex: 1, padding: '0.85rem', borderRadius: 10, cursor: 'pointer',
                        background: 'var(--bnb-gold)', color: '#fff', border: 'none',
                        fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: '0.5rem',
                        boxShadow: '0 2px 12px rgba(200,169,81,0.3)',
                        transition: 'background 0.15s, transform 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background='#b8922a'; e.currentTarget.style.transform='translateY(-1px)' }}
                    onMouseLeave={e => { e.currentTarget.style.background='var(--bnb-gold)'; e.currentTarget.style.transform='translateY(0)' }}>
                        <i className="fa-solid fa-user"></i> My Team Scorecard
                    </button>
                </div>
            )}

            {/* Title row — shown on hero image */}

            {/* Key info + optional Photos card */}
            <div className="bnb-tds-tournament-quick-specs-container" >
                <div style={{ flex: 1, background: '#f8fafc', border: '1.5px solid var(--bnb-border)', borderRadius: 12, padding: '1.1rem 1.25rem' }}>
                    <div className="bnb-tds-tournament-quick-specs-data-container">
                        <InfoRow icon="fa-location-dot"  label="Location"    value={t.location} />
                        <InfoRow icon="fa-users"          label="Format"      value={t.teams} />
                        <InfoRow icon="fa-dollar-sign"    label="Entry Fee"   value={t.entry_fee > 0 ? `$${parseFloat(t.entry_fee).toFixed(2)}` : 'Free'} />
                        <InfoRow icon="fa-user-check"     label="Eligibility" value={t.membership_required ? 'Members only' : 'Open to all'} />
                        {t.max_players && <InfoRow icon="fa-hashtag" label="Max Players" value={String(t.max_players)} />}
                        {t.registered_count > 0 && <InfoRow icon="fa-clipboard-list" label="Registered" value={`${t.registered_count} player${t.registered_count !== 1 ? 's' : ''}`} />}
                    </div>
                </div>

                {/* Photos card — only if album link exists */}
                {t.photo_gallery_url && (
                    <a
                        href={t.photo_gallery_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                            background: 'var(--bnb-navy)', border: '1.5px solid var(--bnb-navy)',
                            borderRadius: 12, padding: '1.1rem 1.75rem',
                            textDecoration: 'none', flexShrink: 0,
                            transition: 'background 0.18s ease, transform 0.15s ease',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#1a4fba'; e.currentTarget.style.transform = 'translateY(-1px)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--bnb-navy)'; e.currentTarget.style.transform = 'translateY(0)' }}
                    >
                        <i className="fa-solid fa-images" style={{ fontSize: '1.6rem', color: 'var(--bnb-gold)' }} />
                        <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.85rem', whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>View Photos</span>
                    </a>
                )}
            </div>

            {/* Two-column body: main left, schedule right */}
            <div className="bnb-tds-two-column-container" style={{ gridTemplateColumns: t.days?.length > 0 ? '1fr 280px' : '1fr'}}>

                {/* ── LEFT COLUMN ── */}
                <div className="bnb-tds-two-column-left-container">
                    {/* Main tab bar */}
                    {(() => {
                        const hasCourses  = (t.days || []).some(d => d.course_id)
                        const hasContests = t.contests?.length > 0
                        const hasAwards   = t.status === 'completed' && t.awards?.length > 0
                        const tabs = [
                            { id: 'about',    label: 'About',    icon: 'fa-circle-info',  show: true },
                            { id: 'courses',  label: 'Courses',  icon: 'fa-flag',         show: hasCourses },
                            { id: 'contests', label: 'Contests', icon: 'fa-bullseye',     show: hasContests },
                            { id: 'awards',   label: 'Awards',   icon: 'fa-medal',        show: hasAwards },
                            { id: 'teams',     label: 'Standings', icon: 'fa-ranking-star', show: (t.teams_list?.length > 0) },
                            { id: 'scorecard', label: 'Scorecard', icon: 'fa-table',        show: (t.scorecards?.length > 0) },
                        ].filter(t => t.show)
                        return (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem', borderBottom: '2px solid var(--bnb-border)', paddingBottom: '0',paddingTop: '10px' }}>
                                {tabs.map(tab => (
                                    <button key={tab.id} onClick={() => setMainTab(tab.id)} className="bnb-tds-tab-button" style={{
                                        
                                        color: mainTab === tab.id ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                                        borderBottom: mainTab === tab.id ? '2.5px solid var(--bnb-navy)' : '2.5px solid transparent',
                                       
                                    }}>
                                        <i className={`fa-solid ${tab.icon}`} style={{ fontSize: '0.72rem' }} />{tab.label}
                                    </button>
                                ))}
                            </div>
                        )
                    })()}

                    {/* ── About tab ── */}
                    {mainTab === 'about' && (
                        <div className="bnb-tab-content">
                            {t.description && (
                                <div style={{ color: '#374151', lineHeight: 1.7, fontSize: '0.9rem', marginBottom: '1.25rem' }}
                                    dangerouslySetInnerHTML={{ __html: t.description }} />
                            )}
                            {t.rules?.length > 0 && (
                                <>
                                    <SectionTitle icon="fa-book">Rules</SectionTitle>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        {t.rules.map((r, i) => (
                                            <div key={i} style={{ background: '#fff', border: '1.5px solid var(--bnb-border)', borderRadius: 10, padding: '0.75rem 1rem' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#111827' }}>{r.name}</div>
                                                {r.description && <div style={{ fontSize: '0.8rem', color: 'var(--bnb-gray)', marginTop: '0.2rem' }}>{r.description}</div>}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {!t.description && !t.rules?.length && (
                                <div style={{ color: 'var(--bnb-gray)', fontSize: '0.9rem' }}>No description available.</div>
                            )}
                        </div>
                    )}

                    {/* ── Contests + Awards tab ── */}
                    {(mainTab === 'contests' || mainTab === 'awards') && (t.contests?.length > 0 || (t.status === 'completed' && t.awards?.length > 0)) && (() => {
                        const hasContests = t.contests?.length > 0
                        const hasAwards   = t.status === 'completed' && t.awards?.length > 0
                        const activeSection = mainTab === 'awards' && hasAwards ? 'awards' : 'contests'
                        return (
                            <div className="bnb-tab-content">

                                {/* ── Contests panel ── */}
                                {activeSection === 'contests' && hasContests && (
                                    <>
                                        {/* Inner tab bar for multiple contests */}
                                        {t.contests.length > 1 && (
                                            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', borderBottom: '1.5px solid var(--bnb-border)', paddingBottom: '0' }}>
                                                {t.contests.map((c, ti) => (
                                                    <button key={ti} onClick={() => setContestTab(ti)} style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        padding: '0.4rem 0.9rem', fontSize: '0.75rem', fontWeight: 700,
                                                        color: contestTab === ti ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                                                        borderBottom: contestTab === ti ? '2px solid var(--bnb-navy)' : '2px solid transparent',
                                                        marginBottom: '-1.5px', whiteSpace: 'nowrap', transition: 'color 0.15s',
                                                    }}>{c.name}</button>
                                                ))}
                                            </div>
                                        )}
                                        {(() => {
                                            const c = t.contests[Math.min(contestTab, t.contests.length - 1)]
                                            return (
                                                <div key={`c-${contestTab}`} className="bnb-tab-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--bnb-border)', background: '#0d1f3c', minHeight: 220 }}>

                                        {/* Background image */}
                                        {c.image_url ? (
                                            <img src={c.image_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        ) : (
                                            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0d2a5e 0%, #040d1f 100%)' }} />
                                        )}

                                        {/* Gradient overlay */}
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 42%, transparent 50%, rgba(0,0,0,0.72) 100%)', pointerEvents: 'none' }} />

                                        {/* Overlay content */}
                                        <div style={{ position: 'relative', minHeight: 220, padding: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

                                            {/* Top: contest name + golf icon */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.55)' }}>{c.name}</div>
                                                <div style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 10, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <i className="fa-solid fa-golf-ball-tee" style={{ fontSize: '1.1rem', color: 'var(--bnb-gold)' }} />
                                                </div>
                                            </div>

                                            {/* Middle: rounds with winner under each */}
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginTop: '0.5rem' }}>
                                                {(c.rounds || []).map((r, ri) => (
                                                    <div key={ri} className="bnb-round-gradient">
                                                        {/* Round + Hole label + View Hole */}
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                                                            <div>
                                                                {r.round && (
                                                                    <span style={{ color: 'var(--bnb-gold)', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', marginRight: '0.5rem' }}>
                                                                        R{r.round}
                                                                    </span>
                                                                )}
                                                                {r.hole && (
                                                                    <span style={{ color: '#fff', fontSize: '2rem', fontWeight: 900, lineHeight: 1, textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>
                                                                        HOLE {r.hole}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {r.hole_data && (
                                                                <span
                                                                    className="bnb-view-hole"
                                                                    onClick={() => setContestHoleModal(r.hole_data)}
                                                                    style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--bnb-gold)', background: 'rgba(200,169,81,0.15)', border: '1px solid rgba(200,169,81,0.55)', borderRadius: 20, padding: '0.15rem 0.6rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                                                >
                                                                    View Hole
                                                                </span>
                                                            )}
                                                        </div>
                                                        {/* Winner directly under this round */}
                                                        {r.winner && (
                                                            <div style={{ color: 'var(--bnb-gold)', fontWeight: 700, fontSize: '0.82rem', textShadow: '0 1px 6px rgba(0,0,0,0.6)', paddingLeft: '0.1rem' }}>
                                                                🏆 Winner: {r.winner}
                                                            </div>
                                                        )}
                                                        {r.result_yards && (
                                                            <div style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600, fontSize: '0.78rem', textShadow: '0 1px 4px rgba(0,0,0,0.6)', paddingLeft: '0.1rem' }}>
                                                                📏 {r.result_yards}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Bottom: prize money only */}
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end', marginTop: '0.5rem' }}>
                                                {(() => {
                                                    const prizes = (c.rounds || []).filter(r => r.prize_money)
                                                    if (!prizes.length) return null
                                                    // If all rounds have the same prize, show once; else show per round
                                                    const allSame = prizes.every(r => r.prize_money === prizes[0].prize_money)
                                                    return (
                                                        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                                                            {allSame ? (
                                                                <>
                                                                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Prize Money</div>
                                                                    <div style={{ color: '#4ade80', fontSize: '1.5rem', fontWeight: 900, lineHeight: 1, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                                                                        {String(prizes[0].prize_money).startsWith('$') ? prizes[0].prize_money : `$${prizes[0].prize_money}`}
                                                                    </div>
                                                                </>
                                                            ) : prizes.map((r, ri) => (
                                                                <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'flex-end' }}>
                                                                    {r.round && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.6rem' }}>R{r.round}</span>}
                                                                    <span style={{ color: '#4ade80', fontSize: '1.1rem', fontWeight: 900, lineHeight: 1 }}>
                                                                        {String(r.prize_money).startsWith('$') ? r.prize_money : `$${r.prize_money}`}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                })()}
                                            </div>

                                        </div>
                                    </div>
                                    </div>
                                )
                            })()}
                                    </>
                                )}

                                {/* ── Awards panel ── */}
                                {activeSection === 'awards' && hasAwards && (
                                    <>
                                        {t.awards.length > 1 && (
                                            <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', borderBottom: '1.5px solid var(--bnb-border)', paddingBottom: '0' }}>
                                                {t.awards.map((a, ti) => (
                                                    <button key={ti} onClick={() => setAwardTab(ti)} style={{
                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                        padding: '0.4rem 0.9rem', fontSize: '0.75rem', fontWeight: 700,
                                                        color: awardTab === ti ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                                                        borderBottom: awardTab === ti ? '2px solid var(--bnb-navy)' : '2px solid transparent',
                                                        marginBottom: '-1.5px', whiteSpace: 'nowrap', transition: 'color 0.15s',
                                                    }}>{a.name}</button>
                                                ))}
                                            </div>
                                        )}
                                        {(() => {
                                            const a = t.awards[Math.min(awardTab, t.awards.length - 1)]
                                            return (
                                                <div key={`a-${awardTab}`} className="bnb-tab-content" style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1.5px solid var(--bnb-border)', background: '#0d1f3c', minHeight: 220 }}>

                                                    {/* Background image */}
                                                    {a.image_url ? (
                                                        <img src={a.image_url} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                    ) : (
                                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #0d2a5e 0%, #040d1f 100%)' }} />
                                                    )}

                                                    {/* Gradient overlay */}
                                                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 42%, transparent 50%, rgba(0,0,0,0.72) 100%)', pointerEvents: 'none' }} />

                                                    {/* Content */}
                                                    <div style={{ position: 'relative', minHeight: 220, padding: '1rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>

                                                        {/* Top: name + winner (top-left) + medal icon (top-right) */}
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                                                                <div style={{ color: '#fff', fontSize: '1.4rem', fontWeight: 900, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.55)' }}>{a.name}</div>
                                                                {a.winner && (
                                                                    <div style={{ color: 'var(--bnb-gold)', fontWeight: 800, fontSize: '1rem', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>
                                                                        🏆 Winner: {a.winner}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 10, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <i className="fa-solid fa-medal" style={{ fontSize: '1.1rem', color: 'var(--bnb-gold)' }} />
                                                            </div>
                                                        </div>

                                                        {/* Description — vertically centered, right two-thirds, tinted bg */}
                                                        {a.description && (
                                                            <div style={{ position: 'absolute', bottom: '1rem', left: '1rem', right: '1rem', maxWidth: '75%' }}>
                                                                <div style={{
                                                                    background: 'rgba(0,0,0,0.52)', backdropFilter: 'blur(6px)',
                                                                    borderLeft: '3px solid var(--bnb-gold)',
                                                                    borderRadius: 8, padding: '0.6rem 0.9rem',
                                                                    color: '#fff', fontSize: '1rem', fontWeight: 700,
                                                                    lineHeight: 1.55, textShadow: '0 1px 4px rgba(0,0,0,0.6)',
                                                                }}>
                                                                    {a.description}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })()}
                                    </>
                                )}
                            </div>
                        )
                    })()}

                    {/* ── Courses tab: course showcase ── */}
                    {mainTab === 'courses' && (() => {
                        const seen = new Set()
                        const courses = (t.days || []).filter(d => {
                            if (!d.course_id || seen.has(d.course_id)) return false
                            seen.add(d.course_id); return true
                        })
                        if (!courses.length) return null
                        const activeTab = Math.min(courseTab, courses.length - 1)
                        const d = courses[activeTab]
                        const fmtDate = s => {
                            if (!s) return ''
                            const [yr, mo, dy] = s.split('-')
                            return new Date(parseInt(yr), parseInt(mo) - 1, parseInt(dy))
                                .toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                        }
                        const fmtTime = s => {
                            if (!s) return ''
                            const [h, m] = s.split(':')
                            const hour = parseInt(h)
                            return `${hour % 12 || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`
                        }
                        return (
                            <>
                                {/* Tab bar — only show if more than one course */}
                                {courses.length > 1 && (
                                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', borderBottom: '2px solid var(--bnb-border)', paddingBottom: '0' }}>
                                        {courses.map((cd, ti) => (
                                            <button
                                                key={ti}
                                                onClick={() => setCourseTab(ti)}
                                                style={{
                                                    background: 'none', border: 'none', cursor: 'pointer',
                                                    padding: '0.5rem 1rem',
                                                    fontSize: '0.8rem', fontWeight: 700,
                                                    color: activeTab === ti ? 'var(--bnb-navy)' : 'var(--bnb-gray)',
                                                    borderBottom: activeTab === ti ? '2.5px solid var(--bnb-navy)' : '2.5px solid transparent',
                                                    marginBottom: '-2px',
                                                    whiteSpace: 'nowrap',
                                                    transition: 'color 0.15s',
                                                }}
                                            >
                                                {cd.course}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {/* Active course card */}
                                <div key={activeTab} className="bnb-tab-content" style={{ borderRadius: 14, overflow: 'hidden', border: '1.5px solid var(--bnb-border)', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

                                    {/* Image hero with overlays */}
                                    <div style={{ position: 'relative', width: '100%', height: 300, background: '#0d1f3c' }}>
                                        {d.course_image_url && (
                                            <img src={d.course_image_url} alt={d.course} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        )}
                                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.58) 0%, transparent 38%, transparent 52%, rgba(0,0,0,0.7) 100%)', pointerEvents: 'none' }} />

                                        {/* Top-left: name + location */}
                                        <div style={{ position: 'absolute', top: '1.1rem', left: '1.25rem', maxWidth: '60%' }}>
                                            <div style={{ color: '#fff', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>{d.course}</div>
                                            {d.course_location && (
                                                <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: '0.8rem', marginTop: '0.2rem', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                                                    <i className="fa-solid fa-location-dot" style={{ marginRight: '0.3rem' }} />{d.course_location}
                                                </div>
                                            )}
                                        </div>

                                        {/* Bottom-right: stats + Show Holes */}
                                        <div style={{ position: 'absolute', bottom: '1.1rem', right: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.6rem' }}>
                                            <div style={{ display: 'flex', gap: '1.1rem', alignItems: 'flex-end' }}>
                                                {d.course_par && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ color: '#fff', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1, textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>{d.course_par}</div>
                                                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Par</div>
                                                    </div>
                                                )}
                                                {d.course_rating && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ color: '#fff', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1, textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>{d.course_rating}</div>
                                                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Rating</div>
                                                    </div>
                                                )}
                                                {d.course_slope && (
                                                    <div style={{ textAlign: 'center' }}>
                                                        <div style={{ color: '#fff', fontSize: '1.45rem', fontWeight: 800, lineHeight: 1, textShadow: '0 2px 6px rgba(0,0,0,0.5)' }}>{d.course_slope}</div>
                                                        <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Slope</div>
                                                    </div>
                                                )}
                                            </div>
                                            {d.course_holes?.length > 0 && (
                                                <button onClick={() => { setCourseModal(d); setModalHoleIdx(0) }}
                                                    style={{ background: 'var(--bnb-gold)', color: '#fff', border: 'none', borderRadius: 8, padding: '0.38rem 0.9rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                                                    <i className="fa-solid fa-flag" style={{ marginRight: '0.4rem' }} />Show Holes
                                                </button>
                                            )}
                                        </div>

                                        {/* Bottom-left: Visit Course Website */}
                                        {d.course_website && (
                                            <div style={{ position: 'absolute', bottom: '1.1rem', left: '1.25rem' }}>
                                                <a href={d.course_website} target="_blank" rel="noopener noreferrer"
                                                    style={{ color: '#fff', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none', background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '0.35rem 0.75rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                                                    <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '0.68rem' }} />View Website
                                                </a>
                                            </div>
                                        )}
                                    </div>

                                    {/* Description below image */}
                                    {d.course_description && (
                                        <div dangerouslySetInnerHTML={{ __html: d.course_description }}
                                            style={{ padding: '1rem 1.25rem', fontSize: '0.88rem', color: '#374151', lineHeight: 1.65, background: '#fff' }} />
                                    )}

                                    {/* Rounds at this course */}
                                    {(() => {
                                        const courseDays = (t.days || []).filter(dd => dd.course_id === d.course_id)
                                        if (!courseDays.length) return null
                                        return (
                                            <div style={{ background: '#f8fafc', borderTop: '1.5px solid var(--bnb-border)', padding: '0.9rem 1.25rem' }}>
                                                <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--bnb-gray)', fontWeight: 700, marginBottom: '0.6rem' }}>
                                                    Rounds at this Course
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                                    {courseDays.map((dd, di) => (
                                                        <div key={di} className="bnb-tds-round-container" style={{  border: '1.5px solid var(--bnb-border)' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                                <span style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--bnb-navy)', whiteSpace: 'nowrap' }}>Round {dd.order}</span>
                                                                {dd.day_date && <span style={{ fontSize: '0.8rem', color: '#374151' }}>{fmtDate(dd.day_date)}</span>}
                                                            </div>
                                                            {(dd.start_time || dd.end_time) && (
                                                                <span style={{ fontSize: '0.78rem', color: 'var(--bnb-gray)', whiteSpace: 'nowrap' }}>
                                                                    {dd.start_time && <><i className="fa-regular fa-clock" style={{ marginRight: '0.3rem' }} />{fmtTime(dd.start_time)}</>}
                                                                    {dd.start_time && dd.end_time && ' – '}
                                                                    {dd.end_time && fmtTime(dd.end_time)}
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })()}

                                </div>
                            </>
                        )
                    })()}

                    {/* ── Scorecard tab ── */}
                    {mainTab === 'scorecard' && t.scorecards?.length > 0 && (
                        <div className="bnb-tab-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {t.scorecards.map((sc, si) => (
                                <div key={si}>
                                    {t.scorecards.length > 1 && (
                                        <div style={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--bnb-navy)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>
                                            Round {sc.round} — {sc.course}
                                        </div>
                                    )}
                                    <div style={{ overflowX: 'auto', borderRadius: 10, border: '1.5px solid var(--bnb-border)', boxShadow: '0 1px 6px rgba(0,48,135,0.06)' }}>
                                        <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.72rem' }}>
                                            <thead>
                                                {/* Hole numbers */}
                                                <tr style={{ background: 'var(--bnb-navy)', color: '#fff' }}>
                                                    <th style={{ padding: '0.45rem 0.75rem', textAlign: 'left', fontWeight: 800, position: 'sticky', left: 0, background: 'var(--bnb-navy)', zIndex: 2, minWidth: 100, borderRight: '2px solid rgba(255,255,255,0.2)' }}>{sc.holes && sc.holes.length > 0 ? (sc.holes[0].number > 9 ? 'Back' : 'Front') : 'Team'}</th>
                                                    {sc.holes.slice(0,9).map((h,hi) => <th key={h.number} onMouseEnter={() => setHoveredCol(hi)} onMouseLeave={() => setHoveredCol(null)} style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 700, minWidth: 32, cursor: 'default', background: hoveredCol === hi ? '#1a4fba' : 'transparent', transition: 'background 0.12s' }}>{h.number}</th>)}
                                                    <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 800, background: '#1a4fba', minWidth: 40 }}>OUT</th>
                                                    {sc.holes.slice(9).map((h,hi) => <th key={h.number} onMouseEnter={() => setHoveredCol(hi+9)} onMouseLeave={() => setHoveredCol(null)} style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 700, minWidth: 32, cursor: 'default', background: hoveredCol === hi+9 ? '#1a4fba' : 'transparent', transition: 'background 0.12s' }}>{h.number}</th>)}
                                                    <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 800, background: '#1a4fba', minWidth: 40 }}>IN</th>
                                                    <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 900, background: '#003087', minWidth: 44 }}>TOT</th>
                                                </tr>
                                                {/* Par row */}
                                                <tr style={{ background: '#f1f5f9', borderBottom: '1px solid var(--bnb-border)' }}>
                                                    <td style={{ padding: '0.35rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase', position: 'sticky', left: 0, background: '#f1f5f9', zIndex: 2, borderRight: '2px solid var(--bnb-border)' }}>Par</td>
                                                    {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, color: '#374151', background: hoveredCol === hi ? 'rgba(0,48,135,0.08)' : 'transparent', transition: 'background 0.1s' }}>{h.par}</td>)}
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{sc.front9_par}</td>
                                                    {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, color: '#374151', background: hoveredCol === hi+9 ? 'rgba(0,48,135,0.08)' : 'transparent', transition: 'background 0.1s' }}>{h.par}</td>)}
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{sc.back9_par}</td>
                                                    <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 900, color: 'var(--bnb-navy)', background: '#dde3ef' }}>{sc.total_par}</td>
                                                </tr>
                                                {/* Handicap row */}
                                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--bnb-border)' }}>
                                                    <td style={{ padding: '0.3rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 2, borderRight: '2px solid var(--bnb-border)' }}>HCP</td>
                                                    {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.3rem 0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.68rem', fontWeight: 600, background: hoveredCol === hi ? 'rgba(0,48,135,0.08)' : 'transparent', transition: 'background 0.1s' }}>{h.handicap ?? '—'}</td>)}
                                                    <td style={{ background: '#e8edf5' }}></td>
                                                    {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.3rem 0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.68rem', fontWeight: 600, background: hoveredCol === hi+9 ? 'rgba(0,48,135,0.08)' : 'transparent', transition: 'background 0.1s' }}>{h.handicap ?? '—'}</td>)}
                                                    <td style={{ background: '#e8edf5' }}></td>
                                                    <td style={{ background: '#dde3ef' }}></td>
                                                </tr>
                                                {/* Yards rows — one per tee */}
                                                {(sc.tees || []).map(tee => {
                                                    const key = `tee_${tee.num}`
                                                    const front9y = sc.holes.slice(0,9).reduce((s,h) => s + (h[key] || 0), 0)
                                                    const back9y  = sc.holes.slice(9).reduce((s,h)  => s + (h[key] || 0), 0)
                                                    const teeTints = {
                                                        black:   'rgba(0,0,0,0.10)',
                                                        blue:    'rgba(37,99,235,0.12)',
                                                        white:   'rgba(241,245,249,0.95)',
                                                        gold:    'rgba(200,169,81,0.16)',
                                                        yellow:  'rgba(253,224,71,0.18)',
                                                        red:     'rgba(220,38,38,0.11)',
                                                        green:   'rgba(22,163,74,0.13)',
                                                        silver:  'rgba(148,163,184,0.18)',
                                                    }
                                                    const tint = teeTints[tee.name.toLowerCase()] || 'rgba(0,0,0,0.06)'
                                                    return (
                                                        <tr key={tee.num} style={{ background: tint, borderBottom: tee.num === 1 ? '2px solid var(--bnb-border)' : '1px solid var(--bnb-border)' }}>
                                                            <td style={{ padding: '0.28rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.62rem', textTransform: 'uppercase', position: 'sticky', left: 0, background: tint, zIndex: 2, borderRight: '2px solid var(--bnb-border)', whiteSpace: 'nowrap' }}>{tee.name}</td>
                                                            {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.28rem 0.5rem', textAlign: 'center', color: '#374151', fontSize: '0.65rem', fontWeight: 600, background: hoveredCol === hi ? 'rgba(0,48,135,0.1)' : 'transparent', transition: 'background 0.1s' }}>{h[key] ?? '—'}</td>)}
                                                            <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 700, color: 'var(--bnb-navy)', background: '#e8edf5', fontSize: '0.65rem' }}>{front9y || '—'}</td>
                                                            {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.28rem 0.5rem', textAlign: 'center', color: '#374151', fontSize: '0.65rem', fontWeight: 600, background: hoveredCol === hi+9 ? 'rgba(0,48,135,0.1)' : 'transparent', transition: 'background 0.1s' }}>{h[key] ?? '—'}</td>)}
                                                            <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 700, color: 'var(--bnb-navy)', background: '#e8edf5', fontSize: '0.65rem' }}>{back9y || '—'}</td>
                                                            <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#dde3ef', fontSize: '0.65rem' }}>{(front9y + back9y) || '—'}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </thead>
                                            <tbody>
                                                {sc.teams.map((tm, ti) => {
                                                    const scoreCell = (score, par) => {
                                                        if (score == null) return { val: 'N/A', bg: 'transparent', color: '#9ca3af' }
                                                        const diff = score - par
                                                        if (diff <= -2) return { val: score, bg: '#15803d', color: '#fff' }      // eagle+
                                                        if (diff === -1) return { val: score, bg: '#4ade80', color: '#14532d' }  // birdie
                                                        if (diff === 0)  return { val: score, bg: 'transparent', color: '#111827' } // par
                                                        if (diff === 1)  return { val: score, bg: '#fed7aa', color: '#7c2d12' }  // bogey
                                                        return               { val: score, bg: '#fca5a5', color: '#7f1d1d' }     // double+
                                                    }
                                                    return (
                                                        <tr key={ti} style={{ background: ti % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid var(--bnb-border)' }}
                                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,48,135,0.04)'}
                                                            onMouseLeave={e => e.currentTarget.style.background = ti % 2 === 0 ? '#fff' : '#f8fafc'}>
                                                            <td style={{ padding: '0.45rem 0.75rem', fontWeight: 700, color: 'var(--bnb-navy)', position: 'sticky', left: 0, background: 'inherit', zIndex: 1, borderRight: '2px solid var(--bnb-border)', whiteSpace: 'nowrap' }}>
                                                                <span style={{ fontSize: '0.6rem', color: 'var(--bnb-gray)', marginRight: '0.3rem' }}>T{tm.number}</span>{tm.name}
                                                            </td>
                                                            {tm.scores.slice(0,9).map((s, hi) => {
                                                                const cell = scoreCell(s, sc.holes[hi]?.par)
                                                                const colHl = hoveredCol === hi ? 'rgba(0,48,135,0.08)' : 'transparent'
                                                                return <td key={hi} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, background: cell.bg !== 'transparent' ? cell.bg : colHl, color: cell.color, borderRadius: cell.bg !== 'transparent' ? 3 : 0, transition: 'background 0.1s' }}>{cell.val}</td>
                                                            })}
                                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{tm.front9 ?? '—'}</td>
                                                            {tm.scores.slice(9).map((s, hi) => {
                                                                const cell = scoreCell(s, sc.holes[hi+9]?.par)
                                                                const colHl = hoveredCol === hi+9 ? 'rgba(0,48,135,0.08)' : 'transparent'
                                                                return <td key={hi+9} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, background: cell.bg !== 'transparent' ? cell.bg : colHl, color: cell.color, borderRadius: cell.bg !== 'transparent' ? 3 : 0, transition: 'background 0.1s' }}>{cell.val}</td>
                                                            })}
                                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{tm.back9 ?? '—'}</td>
                                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 900, color: 'var(--bnb-navy)', background: '#dde3ef' }}>{tm.round_total ?? '—'}</td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ── Standings tab ── */}
                    {mainTab === 'teams' && t.teams_list?.length > 0 && (() => {
                        // Collect all rounds that have scores
                        const allRounds = [...new Set(
                            t.teams_list.flatMap(tm => tm.round_scores.map(r => r.round).filter(r => r != null))
                        )].sort((a, b) => a - b)

                        // Sort teams
                        const sorted = [...t.teams_list].sort((a, b) => {
                            if (teamSort === 'total') {
                                const ta = a.total_score ?? Infinity
                                const tb = b.total_score ?? Infinity
                                return ta - tb
                            }
                            const ra = a.round_scores.find(r => r.round === teamSort)?.total_score ?? Infinity
                            const rb = b.round_scores.find(r => r.round === teamSort)?.total_score ?? Infinity
                            return ra - rb
                        })

                        return (
                            <div className="bnb-tab-content">
                                {/* Sort/filter bar */}
                                {(allRounds.length > 0 || t.teams_list.some(tm => tm.total_score != null)) && (
                                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--bnb-gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: '0.25rem' }}>Sort:</span>
                                        <button onClick={() => setTeamSort('total')} style={{
                                            background: teamSort === 'total' ? 'var(--bnb-navy)' : '#f1f5f9',
                                            color: teamSort === 'total' ? '#fff' : 'var(--bnb-gray)',
                                            border: 'none', borderRadius: 6, padding: '0.3rem 0.75rem',
                                            fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                                        }}>Total</button>
                                        {allRounds.map(r => (
                                            <button key={r} onClick={() => setTeamSort(r)} style={{
                                                background: teamSort === r ? 'var(--bnb-navy)' : '#f1f5f9',
                                                color: teamSort === r ? '#fff' : 'var(--bnb-gray)',
                                                border: 'none', borderRadius: 6, padding: '0.3rem 0.75rem',
                                                fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                                            }}>R{r}</button>
                                        ))}
                                    </div>
                                )}

                                {/* Team cards */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                                    {sorted.map((team, ti) => {
                                        const displayScore = teamSort === 'total'
                                            ? team.total_score
                                            : team.round_scores.find(r => r.round === teamSort)?.total_score
                                        return (
                                            <div key={ti} style={{
                                                width: '100%', background: 'linear-gradient(to top, #e2e8f0 0%, #fff 100%)',
                                                border: '1.5px solid var(--bnb-border)',
                                                borderRadius: 12, overflow: 'hidden',
                                                display: 'flex', alignItems: 'stretch',
                                                boxShadow: '0 1px 6px rgba(0,48,135,0.06)',
                                            }}>
                                                {/* Left navy: T# + combined HCP */}
                                                <div style={{
                                                    background: 'var(--bnb-navy)', flexShrink: 0, width: 80,
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                    padding: '1rem 0.5rem', gap: '0.3rem',
                                                }}>
                                                    <div style={{ color: '#fff', fontWeight: 900, fontSize: '1.8rem', lineHeight: 1, letterSpacing: '-0.02em' }}>
                                                        T{team.number}
                                                    </div>
                                                    {(team.hcp_sum != null || team.handicap) && (
                                                        <div style={{ color: 'var(--bnb-gold)', fontWeight: 700, fontSize: '0.68rem', textAlign: 'center', lineHeight: 1.3 }}>
                                                            HCP<br/>{team.hcp_sum ?? team.handicap}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Stats columns: Total + per-round */}
                                                {(() => {
                                                    const cols = [
                                                        { label: 'Total', value: displayScore, par: team.total_par },
                                                        ...team.round_scores.map(r => ({ label: `R${r.round}`, value: r.total_score, par: r.par })),
                                                    ]
                                                    return (
                                                        <div style={{ display: 'flex', alignItems: 'stretch', borderRight: '1.5px solid var(--bnb-border)' }}>
                                                            {cols.map((col, ci) => (
                                                                <div key={ci} className={ci !== 0 ? "mobileHide" : ""} style={{
                                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                                    padding: '0.75rem 1.1rem', minWidth: 70,
                                                                    borderLeft: ci > 0 ? '1px solid var(--bnb-border)' : 'none',
                                                                }}>
                                                                    <div  style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                                                                        <span style={{ fontWeight: 900, fontSize: ci === 0 ? '2rem' : '1.4rem', color: ci === 0 ? 'var(--bnb-navy)' : '#374151', lineHeight: 1 }}>
                                                                            {col.value ?? '—'}
                                                                        </span>
                                                                    </div>
                                                                    <div style={{ fontSize: '0.6rem', fontWeight: 700, color: 'var(--bnb-gray)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                                                                        {col.label}{col.par != null && <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>/ {col.par}</span>}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )
                                                })()}

                                                {/* Team name */}
                                                <div className="mobileHide" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0.75rem 1rem', minWidth: 0 }}>
                                                    <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--bnb-navy)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{team.name}</div>
                                                </div>

                                                {/* Right: overlapping portraits */}
                                                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'right', width: '100%', overflow: 'hidden', paddingRight: '0.5rem' }}>
                                                    {team.members.map((m, mi) => (
                                                        <div key={mi} style={{ position: 'relative', flexShrink: 0, marginLeft: mi > 0 ? '-18px' : 0, zIndex: team.members.length - mi }}>
                                                            <img src={m.image_url || '/bnb-profile-default.png'} alt={m.name}
                                                                className="bnb-tsd-standings-player-image" style={{  width: 'auto', display: 'block', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.2))' }} />
                                                            <div style={{ position: 'absolute', bottom: '0.2rem', left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
                                                                <div style={{
                                                                    background: 'var(--bnb-navy)', border: '1px solid var(--bnb-gold)',
                                                                    borderRadius: 999, padding: '0.05rem 0.4rem',
                                                                    color: '#fff', fontWeight: 800, fontStyle: 'italic', fontSize: '0.52rem',
                                                                    whiteSpace: 'nowrap', maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis',
                                                                }}>{m.name.split(' ')[0]}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )
                    })()}
                </div>

                {/* ── RIGHT COLUMN: Schedule ── */}
                {t.days?.length > 0 && (
                    <div className="bnb-tds-two-column-right-container">

                        {/* Accountant / Payment section */}
                        {t.accountant && (
                            <>
                                <SectionTitle icon="fa-wallet">Payment</SectionTitle>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
                                    <div style={{
                                        background: '#fff', border: '1.5px solid var(--bnb-border)',
                                        borderRadius: 10, padding: '0.75rem 1rem',
                                    }}>
                                        <div style={{ fontWeight: 800, color: 'var(--bnb-navy)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                                            Accountant
                                        </div>
                                        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#111827', marginBottom: '0.4rem' }}>
                                            {t.accountant.name}
                                        </div>
                                        {t.accountant.payment_methods.length > 0 ? (
                                            <>
                                                {t.accountant.payment_methods.map((pm, i) => (
                                                    <div key={i} style={{ marginBottom: '0.2rem' }}>
                                                        <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--bnb-gray)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{pm.method}: </span>
                                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#111827' }}>{pm.details}</span>
                                                    </div>
                                                ))}
                                                <div style={{ marginTop: '0.55rem', fontSize: '0.7rem', color: 'var(--bnb-gray)', lineHeight: 1.5, borderTop: '1px solid var(--bnb-border)', paddingTop: '0.45rem' }}>
                                                    All entry fee payments go to this account. If you do not have access to this payment method, contact the accountant directly.
                                                </div>
                                            </>
                                        ) : (
                                            <div style={{ fontSize: '0.75rem', color: 'var(--bnb-gray)' }}>Contact the accountant for payment details.</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        <SectionTitle icon="fa-calendar-days">Schedule</SectionTitle>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {t.days.map((d) => (
                                <div key={d.id} style={{
                                    background: '#fff', border: '1.5px solid var(--bnb-border)',
                                    borderRadius: 10, padding: '0.75rem 1rem',
                                }}>
                                    <div style={{ fontWeight: 800, color: 'var(--bnb-navy)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.25rem' }}>
                                        Day {d.order}
                                    </div>
                                    <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#111827' }}>{formatDate(d.day_date)}</div>
                                    {d.course && (
                                        <div style={{ fontSize: '0.75rem', color: 'var(--bnb-gray)', marginTop: '0.2rem' }}>
                                            <i className="fa-solid fa-flag me-1"></i>{d.course}
                                        </div>
                                    )}
                                    {(d.start_time || d.end_time) && (
                                        <div style={{ fontSize: '0.75rem', color: 'var(--bnb-gray)', fontWeight: 600, marginTop: '0.2rem' }}>
                                            <i className="fa-solid fa-clock me-1"></i>
                                            {formatTime(d.start_time)}{d.end_time ? ` – ${formatTime(d.end_time)}` : ''}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>{/* end two-column grid */}
        </div>

        {/* ── Live Standings Modal ── */}
        {liveModal === 'standings' && t.teams_list?.length > 0 && (
            <div onClick={() => setLiveModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.5)' }}>
                    {/* Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', borderBottom: '1.5px solid var(--bnb-border)', background: 'var(--bnb-navy)' }}>
                        <div style={{ color: '#fff', fontWeight: 900, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <i className="fa-solid fa-table" style={{ color: 'var(--bnb-gold)' }} /> Live Scorecard
                        </div>
                        <button onClick={() => setLiveModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    </div>
                    {/* Body */}
                    <div style={{ overflowY: 'auto', padding: '1rem' }}>
                        {[...t.teams_list].sort((a,b) => (a.total_score ?? Infinity) - (b.total_score ?? Infinity)).map((team, ti) => (
                            <div key={ti} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.8rem', borderRadius: 10, marginBottom: '0.75rem', background: ti === 0 ? 'rgba(200,169,81,0.08)' : '#f8fafc', border: `1.5px solid ${ti === 0 ? 'rgba(200,169,81,0.4)' : 'var(--bnb-border)'}` }}>
                                {/* Rank Badge */}
                                <div style={{ width: 36, height: 36, borderRadius: '50%', background: ti === 0 ? 'var(--bnb-gold)' : ti === 1 ? '#94a3b8' : ti === 2 ? '#cd7f32' : 'var(--bnb-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: '0.85rem', color: ti < 3 ? '#fff' : 'var(--bnb-gray)', flexShrink: 0 }}>
                                    {ti + 1}
                                </div>

                                {/* Team Info (Name, Scores) */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 0.8, minWidth: 0 }}>
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 800, color: 'var(--bnb-navy)', fontSize: '0.9rem', marginBottom: '0.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            {team.name}
                                            {team.id === t.user_team_id && (
                                                <span style={{ background: 'var(--bnb-gold)', color: 'var(--bnb-navy)', fontSize: '0.65rem', fontWeight: 900, padding: '0.2rem 0.5rem', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>YOU</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '0.65rem', color: 'var(--bnb-gray)', fontWeight: 600 }}>
                                            {team.round_scores.map(r => `R${r.round}: ${r.total_score ?? '—'}`).join(' · ')}
                                        </div>
                                    </div>

                                    {/* Player Avatars with Country Badges */}
                                    {team.members && team.members.length > 0 && (
                                        <div style={{ display: 'flex', alignItems: 'flex-end', overflow: 'visible', gap: '0.2rem', flexShrink: 0 }}>
                                            {team.members.map((member, mi) => (
                                                <PlayerImageBadge
                                                    key={mi}
                                                    imageUrl={member.image_url}
                                                    playerName={member.name}
                                                    country={member.country || member.birth_place}
                                                    size={32}
                                                    badgeSize={18}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* To-Par Score */}
                                {team.total_score && team.total_par && (() => {
                                    const toPar = team.total_score - team.total_par
                                    const toParDisplay = toPar === 0 ? 'E' : toPar < 0 ? toPar : `+${toPar}`
                                    return (
                                        <div style={{ fontWeight: 900, fontSize: '2rem', color: toPar > 0 ? '#dc2626' : toPar < 0 ? '#16a34a' : '#2563eb', flexShrink: 0, lineHeight: 1, marginLeft: 'auto' }}>
                                            {toParDisplay}
                                        </div>
                                    )
                                })()}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {/* ── Live Scorecard Modal ── */}
        {liveModal === 'scorecard' && t.scorecards?.length > 0 && (() => {
            const sc = t.scorecards[liveScorecardRound] || t.scorecards[0]
            const dayId = sc.day_id
            
            const courseId = sc.course_id
            const userTeamObj = t.user_team_id ? t.teams_list?.find(tl => tl.id === t.user_team_id) : null
            
            const saveScore = async (holeNum, val) => {
                if (!userInfo) return
                const key = `${userTeamObj?.id}_${dayId}_${holeNum}`
                setLiveScores(prev => ({ ...prev, [key]: val }))
                try {
                    await axios.post(`/api/tournaments/public/${t.id}/live/score/`, {
                        team_id: userTeamObj?.id, day_id: dayId, course_id: courseId, hole_number: holeNum, score: val,
                    }, { headers: { Authorization: `Bearer ${userInfo.token}` } })
                    const res = await axios.get(`/api/tournaments/public/${t.id}/team-results/`, {
                        params: { team_id: userTeamObj?.id },
                        headers: { Authorization: `Bearer ${userInfo.token}` }
                    })
                    const resultsMap = {}
                    if (res.data.results) {
                        res.data.results.forEach(r => {
                            const k = `${r.team_id}_${r.day_id}`
                            resultsMap[k] = r
                        })
                    }
                    setTeamResults(resultsMap)
                } catch(e) { /* silent */ }
            }
            
            const getScore = (holeNum, fallback) => {
                const key = `${userTeamObj?.id}_${dayId}_${holeNum}`
                return liveScores[key] !== undefined ? liveScores[key] : fallback
            }

            // Score color helper
            const scoreCell = (score, par) => {
                if (score == null) return { val: 'N/A', bg: 'transparent', color: '#9ca3af' }
                const diff = score - par
                if (diff <= -2) return { val: score, bg: '#15803d', color: '#fff' }
                if (diff === -1) return { val: score, bg: '#4ade80', color: '#14532d' }
                if (diff === 0)  return { val: score, bg: 'transparent', color: '#111827' }
                if (diff === 1)  return { val: score, bg: '#fed7aa', color: '#7c2d12' }
                return               { val: score, bg: '#fca5a5', color: '#7f1d1d' }
            }

            // Calculate team totals
            const totalScore = t.scorecards.reduce((sum, s) => {
                const resultKey = `${t.user_team_id}_${s.day_id}`
                const result = teamResults[resultKey]
                return sum + (result?.total_score ?? 0)
            }, 0)

            const totalPar = t.days.reduce((sum, d) => {
                return sum + (d.course_par ?? 0)
            }, 0)

            return (
                <div onClick={() => setLiveModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem' }}>
                    <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 1000, maxHeight: '94vh', display: 'flex', flexDirection: 'column', overflow: 'visible', boxShadow: '0 32px 80px rgba(0,0,0,0.5)', position: 'relative', zIndex: 10000 }}>
                        {/* Header */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1.5px solid var(--bnb-border)', background: 'var(--bnb-navy)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <div style={{ color: '#fff', fontWeight: 900, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fa-solid fa-user" style={{ color: 'var(--bnb-gold)' }} /> My Team Scorecard
                                    </div>
                                    {userTeamObj && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem', fontWeight: 700, borderLeft: '2px solid rgba(255,255,255,0.2)', paddingLeft: '1rem' }}>
                                                {userTeamObj.name}
                                            </div>
                                            {/* Team Members Icons */}
                                            {userTeamObj.members && userTeamObj.members.length > 0 && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                                                    {userTeamObj.members.map((member, mi) => (
                                                        <PlayerImageBadge
                                                            key={mi}
                                                            imageUrl={member.image_url}
                                                            playerName={member.name}
                                                            country={member.country || member.birth_place}
                                                            size={24}
                                                            badgeSize={14}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                            {/* Team Total Score */}
                                            <div style={{ color: 'var(--bnb-gold)', fontSize: '0.95rem', fontWeight: 700, marginLeft: '1rem', paddingLeft: '1rem', borderLeft: '2px solid rgba(255,255,255,0.2)' }}>
                                                {totalScore} / {totalPar}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => setLiveModal(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                            </div>
                        </div>

                        {/* Round Tabs */}
                        {t.scorecards.length > 1 && (
                            <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid var(--bnb-border)', background: '#f8fafc', padding: '0 1rem', flexShrink: 0 }}>
                                {t.scorecards.map((s, si) => {
                                    const resultKey = `${t.user_team_id}_${s.day_id}`
                                    const result = teamResults[resultKey]
                                    const roundScore = result?.total_score ?? '—'
                                    return (
                                        <button key={si} onClick={() => setLiveScorecardRound(si)} style={{
                                            background: liveScorecardRound === si ? 'var(--bnb-navy)' : 'transparent',
                                            color: liveScorecardRound === si ? '#fff' : 'var(--bnb-gray)',
                                            border: 'none', borderBottom: liveScorecardRound === si ? '3px solid var(--bnb-gold)' : 'none',
                                            padding: '0.75rem 1.2rem', fontWeight: liveScorecardRound === si ? 800 : 700,
                                            fontSize: '0.9rem', cursor: 'pointer',
                                            transition: 'all 0.15s ease',
                                            display: 'flex', alignItems: 'center', gap: '0.4rem',
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = liveScorecardRound === si ? 'var(--bnb-navy)' : 'rgba(0,30,87,0.05)'}
                                        onMouseLeave={e => e.currentTarget.style.background = liveScorecardRound === si ? 'var(--bnb-navy)' : 'transparent'}
                                        >
                                            <i className="fa-solid fa-flag" style={{ fontSize: '0.8rem', color: 'var(--bnb-gold)' }} />
                                            R{s.round} | {roundScore} | {s.course || 'Course'}
                                        </button>
                                    )
                                })}
                            </div>
                        )}

                        {/* Scrollable Content Area */}
                        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'visible', display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem 1rem' }}>
                            {/* Scorecard Table - Front 9 */}
                            <div style={{ overflowX: 'auto', borderRadius: 10, border: '1.5px solid var(--bnb-border)', boxShadow: '0 1px 6px rgba(0,48,135,0.06)' }}>
                                <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.72rem' }}>
                                    <thead>
                                        {/* Hole numbers */}
                                        <tr style={{ background: 'var(--bnb-navy)', color: '#fff' }}>
                                            <th style={{ padding: '0.45rem 0.75rem', textAlign: 'left', fontWeight: 800 }}>Front</th>
                                            {sc.holes.slice(0,9).map((h,hi) => <th key={h.number} style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 700, minWidth: 32 }}>{h.number}</th>)}
                                            <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 800, background: '#1a4fba', minWidth: 40 }}>OUT</th>
                                        </tr>
                                        {/* Par row */}
                                        <tr style={{ background: '#f1f5f9', borderBottom: '1px solid var(--bnb-border)' }}>
                                            <td style={{ padding: '0.35rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase' }}>Par</td>
                                            {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, color: '#374151' }}>{h.par}</td>)}
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{sc.front9_par}</td>
                                        </tr>
                                        {/* Handicap row */}
                                        {sc.holes.some(h => h.handicap) && (
                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--bnb-border)' }}>
                                                <td style={{ padding: '0.3rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase' }}>HCP</td>
                                                {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.3rem 0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.68rem', fontWeight: 600 }}>{h.handicap ?? '—'}</td>)}
                                                <td style={{ background: '#e8edf5' }}></td>
                                            </tr>
                                        )}
                                        {/* Tee rows */}
                                        {(sc.tees || []).map(tee => {
                                            const key = `tee_${tee.num}`
                                            const front9y = sc.holes.slice(0,9).reduce((s,h) => s + (h[key] || 0), 0)
                                            const teeName = tee.name.toLowerCase()
                                            const teeBg = { 'blue': '#003087', 'white': '#ffffff', 'yellow': '#fbbf24', 'red': '#dc2626', 'copper': '#B87333', 'black': '#000000', 'gold': '#FFD700' }[teeName] || '#ffffff'
                                            const teeTint = { 'blue': 'rgba(37,99,235,0.12)', 'white': 'rgba(241,245,249,0.95)', 'yellow': 'rgba(253,224,71,0.18)', 'red': 'rgba(220,38,38,0.11)', 'copper': 'rgba(184,115,51,0.08)', 'black': 'rgba(0,0,0,0.08)', 'gold': 'rgba(255,215,0,0.08)' }[teeName] || 'rgba(0,0,0,0.06)'
                                            return (
                                                <tr key={tee.num} style={{ background: teeTint, borderBottom: tee.num === 1 ? '2px solid var(--bnb-border)' : '1px solid var(--bnb-border)' }}>
                                                    <td style={{ padding: '0.28rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.62rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }} title={tee.name}>
                                                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: teeBg, border: '1px solid #ccc', marginRight: '0.3rem', verticalAlign: 'middle' }} />
                                                    </td>
                                                    {sc.holes.slice(0,9).map((h,hi) => <td key={h.number} style={{ padding: '0.28rem 0.5rem', textAlign: 'center', color: '#374151', fontSize: '0.65rem', fontWeight: 600 }}>{h[key] ?? '—'}</td>)}
                                                    <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 700, color: 'var(--bnb-navy)', background: '#e8edf5', fontSize: '0.65rem' }}>{front9y || '—'}</td>
                                                </tr>
                                            )
                                        })}
                                    </thead>
                                    <tbody>
                                        {/* User's team - Front 9 row */}
                                        <tr style={{ background: '#fff', borderBottom: '1px solid var(--bnb-border)' }}>
                                            <td style={{ padding: '0.45rem 0.75rem', fontWeight: 700, color: 'var(--bnb-navy)' }}>{userTeamObj?.name}</td>
                                            {/* Front 9 scores */}
                                            {sc.holes.slice(0,9).map((h, hi) => {
                                                const saved = sc.teams.find(tm => tm.name === userTeamObj?.name)?.scores[hi]
                                                const current = getScore(hi+1, saved)
                                                const par = h.par
                                                const cell = scoreCell(current != null && current !== '' ? parseInt(current) : null, par)
                                                return (
                                                    <td key={hi} style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                                                        {userInfo ? (
                                                            <input type="number" min="1" max="15"
                                                                value={current ?? ''}
                                                                onChange={e => setLiveScores(prev => ({ ...prev, [`${userTeamObj?.id}_${dayId}_${hi+1}`]: e.target.value }))}
                                                                onBlur={e => saveScore(hi+1, e.target.value)}
                                                                style={{ width: '100%', textAlign: 'center', border: `1.5px solid ${cell.bg !== 'transparent' ? cell.bg : 'var(--bnb-border)'}`, borderRadius: 5, padding: '0.4rem 0.2rem', fontSize: '0.8rem', fontWeight: 700, background: cell.bg, color: cell.color, outline: 'none' }}
                                                            />
                                                        ) : (
                                                            <span style={{ display: 'block', padding: '0.4rem 0.2rem', background: cell.bg, color: cell.color, borderRadius: 4, fontWeight: 700 }}>{cell.val}</span>
                                                        )}
                                                    </td>
                                                )
                                            })}
                                            {/* Front 9 total */}
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>
                                                {sc.holes.slice(0,9).reduce((s,_,hi) => { const v = parseInt(getScore(hi+1, sc.teams.find(tm => tm.name === userTeamObj?.name)?.scores[hi])); return s + (isNaN(v) ? 0 : v) }, 0) || '—'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            {/* Scorecard Table - Back 9 */}
                            <div style={{ overflowX: 'auto', borderRadius: 10, border: '1.5px solid var(--bnb-border)', boxShadow: '0 1px 6px rgba(0,48,135,0.06)' }}>
                                <table style={{ borderCollapse: 'collapse', minWidth: '100%', fontSize: '0.72rem' }}>
                                    <thead>
                                        {/* Hole numbers */}
                                        <tr style={{ background: 'var(--bnb-navy)', color: '#fff' }}>
                                            <th style={{ padding: '0.45rem 0.75rem', textAlign: 'left', fontWeight: 800 }}>Back</th>
                                            {sc.holes.slice(9).map((h,hi) => <th key={h.number} style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 700, minWidth: 32 }}>{h.number}</th>)}
                                            <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 800, background: '#1a4fba', minWidth: 40 }}>IN</th>
                                            <th style={{ padding: '0.45rem 0.5rem', textAlign: 'center', fontWeight: 900, background: '#003087', minWidth: 44 }}>TOT</th>
                                        </tr>
                                        {/* Par row */}
                                        <tr style={{ background: '#f1f5f9', borderBottom: '1px solid var(--bnb-border)' }}>
                                            <td style={{ padding: '0.35rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase' }}>Par</td>
                                            {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, color: '#374151' }}>{h.par}</td>)}
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>{sc.back9_par}</td>
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 900, color: 'var(--bnb-navy)', background: '#dde3ef' }}>{sc.total_par}</td>
                                        </tr>
                                        {/* Handicap row */}
                                        {sc.holes.some(h => h.handicap) && (
                                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid var(--bnb-border)' }}>
                                                <td style={{ padding: '0.3rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.65rem', textTransform: 'uppercase' }}>HCP</td>
                                                {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.3rem 0.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.68rem', fontWeight: 600 }}>{h.handicap ?? '—'}</td>)}
                                                <td style={{ background: '#e8edf5' }}></td>
                                                <td style={{ background: '#dde3ef' }}></td>
                                            </tr>
                                        )}
                                        {/* Tee rows */}
                                        {(sc.tees || []).map(tee => {
                                            const key = `tee_${tee.num}`
                                            const back9y = sc.holes.slice(9).reduce((s,h) => s + (h[key] || 0), 0)
                                            const total = sc.holes.reduce((s,h) => s + (h[key] || 0), 0)
                                            const teeName = tee.name.toLowerCase()
                                            const teeBg = { 'blue': '#003087', 'white': '#ffffff', 'yellow': '#fbbf24', 'red': '#dc2626', 'copper': '#B87333', 'black': '#000000', 'gold': '#FFD700' }[teeName] || '#ffffff'
                                            const teeTint = { 'blue': 'rgba(37,99,235,0.12)', 'white': 'rgba(241,245,249,0.95)', 'yellow': 'rgba(253,224,71,0.18)', 'red': 'rgba(220,38,38,0.11)', 'copper': 'rgba(184,115,51,0.08)', 'black': 'rgba(0,0,0,0.08)', 'gold': 'rgba(255,215,0,0.08)' }[teeName] || 'rgba(0,0,0,0.06)'
                                            return (
                                                <tr key={tee.num} style={{ background: teeTint, borderBottom: tee.num === 1 ? '2px solid var(--bnb-border)' : '1px solid var(--bnb-border)' }}>
                                                    <td style={{ padding: '0.28rem 0.75rem', fontWeight: 700, color: 'var(--bnb-gray)', fontSize: '0.62rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }} title={tee.name}>
                                                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: teeBg, border: '1px solid #ccc', marginRight: '0.3rem', verticalAlign: 'middle' }} />
                                                    </td>
                                                    {sc.holes.slice(9).map((h,hi) => <td key={h.number} style={{ padding: '0.28rem 0.5rem', textAlign: 'center', color: '#374151', fontSize: '0.65rem', fontWeight: 600 }}>{h[key] ?? '—'}</td>)}
                                                    <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 700, color: 'var(--bnb-navy)', background: '#e8edf5', fontSize: '0.65rem' }}>{back9y || '—'}</td>
                                                    <td style={{ padding: '0.28rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#dde3ef', fontSize: '0.65rem' }}>{total || '—'}</td>
                                                </tr>
                                            )
                                        })}
                                    </thead>
                                    <tbody>
                                        {/* User's team - Back 9 row */}
                                        <tr style={{ background: '#fff', borderBottom: '1px solid var(--bnb-border)' }}>
                                            <td style={{ padding: '0.45rem 0.75rem', fontWeight: 700, color: 'var(--bnb-navy)' }}>{userTeamObj?.name}</td>
                                            {/* Back 9 scores */}
                                            {sc.holes.slice(9).map((h, hi) => {
                                                const saved = sc.teams.find(tm => tm.name === userTeamObj?.name)?.scores[hi+9]
                                                const current = getScore(hi+10, saved)
                                                const par = h.par
                                                const cell = scoreCell(current != null && current !== '' ? parseInt(current) : null, par)
                                                return (
                                                    <td key={hi+9} style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>
                                                        {userInfo ? (
                                                            <input type="number" min="1" max="15"
                                                                value={current ?? ''}
                                                                onChange={e => setLiveScores(prev => ({ ...prev, [`${userTeamObj?.id}_${dayId}_${hi+10}`]: e.target.value }))}
                                                                onBlur={e => saveScore(hi+10, e.target.value)}
                                                                style={{ width: '100%', textAlign: 'center', border: `1.5px solid ${cell.bg !== 'transparent' ? cell.bg : 'var(--bnb-border)'}`, borderRadius: 5, padding: '0.4rem 0.2rem', fontSize: '0.8rem', fontWeight: 700, background: cell.bg, color: cell.color, outline: 'none' }}
                                                            />
                                                        ) : (
                                                            <span style={{ display: 'block', padding: '0.4rem 0.2rem', background: cell.bg, color: cell.color, borderRadius: 4, fontWeight: 700 }}>{cell.val}</span>
                                                        )}
                                                    </td>
                                                )
                                            })}
                                            {/* Back 9 total */}
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--bnb-navy)', background: '#e8edf5' }}>
                                                {sc.holes.slice(9).reduce((s,_,hi) => { const v = parseInt(getScore(hi+10, sc.teams.find(tm => tm.name === userTeamObj?.name)?.scores[hi+9])); return s + (isNaN(v) ? 0 : v) }, 0) || '—'}
                                            </td>
                                            {/* Overall total */}
                                            <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 900, color: 'var(--bnb-navy)', background: '#dde3ef' }}>
                                                {sc.holes.reduce((s,_,hi) => { const v = parseInt(getScore(hi+1, sc.teams.find(tm => tm.name === userTeamObj?.name)?.scores[hi])); return s + (isNaN(v) ? 0 : v) }, 0) || '—'}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        {/* End of Scrollable Content Area */}
                    </div>
                </div>
            )

        })()}

        {/* ── Contest hole modal ── */}
        {contestHoleModal && (() => {
            const hd = contestHoleModal
            return (
                <div onClick={() => setContestHoleModal(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ background: '#0a1628', borderRadius: 20, width: '100%', maxWidth: 820, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}>

                        {/* Header strip */}
                        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'space-between', padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                            <div style={{ color: 'var(--bnb-gold)', fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.14em' }}>{hd.course_name}</div>
                            <button onClick={() => setContestHoleModal(null)}
                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                        </div>

                        {/* Body: left stats + right image */}
                        <div  className="bnb-tds-modal-two-column-container">

                            {/* Left panel */}
                            <div className="bnb-tds-contest-modal-coulmn-left-container">
                                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '0.2rem' }}>Hole</div>
                                <div style={{ color: 'var(--bnb-gold)', fontSize: '5.5rem', fontWeight: 900, lineHeight: 1, marginBottom: '1.25rem' }}>{hd.hole_number}</div>

                                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.75rem' }}>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.1rem' }}>Par</div>
                                        <div style={{ color: '#fff', fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>{hd.hole_par}</div>
                                    </div>
                                    {hd.hole_handicap != null && (
                                        <div>
                                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.1rem' }}>Handicap</div>
                                            <div style={{ color: '#fff', fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>{hd.hole_handicap}</div>
                                        </div>
                                    )}
                                </div>

                                {hd.tees?.length > 0 && (
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.6rem' }}>Distances</div>
                                        {hd.tees.map((tee, ti) => (
                                            <div key={ti} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}>{tee.name}</span>
                                                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{tee.yards}<span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', marginLeft: '0.25rem' }}>yds</span></span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {hd.hole_notes && (
                                    <div style={{ background: 'rgba(200,169,81,0.1)', border: '1px solid rgba(200,169,81,0.22)', borderRadius: 8, padding: '0.65rem 0.75rem', fontSize: '0.75rem', color: 'rgba(255,224,120,0.85)', fontStyle: 'italic', lineHeight: 1.5 }}>
                                        {hd.hole_notes}
                                    </div>
                                )}
                            </div>

                            {/* Right: hole image */}
                            <div style={{ flex: 1, background: '#000', position: 'relative', overflow: 'hidden' }}>
                                {hd.hole_image_url ? (
                                    <img src={hd.hole_image_url} alt={`Hole ${hd.hole_number}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d2a5e 0%, #040d1f 100%)' }}>
                                        <i className="fa-solid fa-golf-ball-tee" style={{ fontSize: '4rem', color: 'rgba(200,169,81,0.2)', marginBottom: '1rem' }} />
                                        <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.85rem' }}>No image available</div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            )
        })()}

        {/* ── Holes scorecard modal ── */}
        {courseModal && (() => {
            const holes = courseModal.course_holes || []
            const h = holes[modalHoleIdx] || {}
            const total = holes.length

            return (
                <div onClick={() => setCourseModal(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
                    <div onClick={e => e.stopPropagation()}
                        style={{ background: '#0a1628', borderRadius: 20, width: '100%', maxWidth: 920, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)' }}>

                        {/* ── Hole picker strip ── */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.55rem', padding: '0.75rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                            <div style={{ display: 'flex', flexDirection: 'row', gap: '0.55rem', color: 'var(--bnb-gold)', fontSize: '0.6rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.14em', whiteSpace: 'nowrap' }}>
                                {courseModal.course}
                                <button onClick={() => setCourseModal(null)}
                                style={{ flexShrink: 0, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', width: 30, height: 30, borderRadius: '50%', fontSize: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                            </div>
                            <div style={{ flex: 1, display: 'flex', gap: '0.3rem', flexWrap: 'wrap', overflowX: 'auto', paddingBottom: '0.05rem' }}>
                                {holes.map((hh, idx) => {
                                    const active = idx === modalHoleIdx
                                    return (
                                        <button key={idx} onClick={() => setModalHoleIdx(idx)}
                                            className="bnb-tds-modal-holes-btn-container"
                                            style={{ border: active ? 'none' : '1px solid rgba(255,255,255,0.12)', background: active ? 'var(--bnb-gold)' : 'transparent', color: active ? '#fff' : 'rgba(255,255,255,0.5)', fontWeight: active ? 800 : 400, fontSize: '0.8rem',  }}>
                                            {hh.number}
                                        </button>
                                    )
                                })}
                            </div>
                            
                        </div>

                        {/* ── Body: left stats + right image ── */}
                        <div className="bnb-tds-modal-two-column-container">

                            {/* Left panel */}
                            <div className="bnb-tds-course-modal-coulmn-left-container">

                                <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.16em', marginBottom: '0.2rem' }}>Hole</div>
                                <div style={{ color: 'var(--bnb-gold)', fontSize: '5.5rem', fontWeight: 900, lineHeight: 1, marginBottom: '1.25rem' }}>{h.number}</div>

                                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.75rem' }}>
                                    <div>
                                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.1rem' }}>Par</div>
                                        <div style={{ color: '#fff', fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>{h.par}</div>
                                    </div>
                                    {h.handicap != null && (
                                        <div>
                                            <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.1rem' }}>Handicap</div>
                                            <div style={{ color: '#fff', fontSize: '2.25rem', fontWeight: 800, lineHeight: 1 }}>{h.handicap}</div>
                                        </div>
                                    )}
                                </div>

                                {h.tees?.length > 0 && (
                                    <div style={{ marginBottom: '1.5rem' }}>
                                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '0.6rem' }}>Distances</div>
                                        {h.tees.map((tee, ti) => (
                                            <div key={ti} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem' }}>{tee.name}</span>
                                                <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.9rem' }}>{tee.yards}<span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.35)', marginLeft: '0.25rem' }}>yds</span></span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {h.notes && (
                                    <div style={{ background: 'rgba(200,169,81,0.1)', border: '1px solid rgba(200,169,81,0.22)', borderRadius: 8, padding: '0.65rem 0.75rem', marginBottom: '1rem', fontSize: '0.75rem', color: 'rgba(255,224,120,0.85)', fontStyle: 'italic', lineHeight: 1.5 }}>
                                        {h.notes}
                                    </div>
                                )}

                                <div className="bnb-tds-modal-course-column-left-next-prev-container">
                                    <button onClick={() => setModalHoleIdx(i => Math.max(0, i - 1))} disabled={modalHoleIdx === 0}
                                        style={{ flex: 1, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: modalHoleIdx === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.8)', borderRadius: 8, padding: '0.5rem', fontWeight: 700, fontSize: '0.82rem', cursor: modalHoleIdx === 0 ? 'not-allowed' : 'pointer' }}>
                                        ← Prev
                                    </button>
                                    <button onClick={() => setModalHoleIdx(i => Math.min(total - 1, i + 1))} disabled={modalHoleIdx === total - 1}
                                        style={{ flex: 1, background: modalHoleIdx === total - 1 ? 'rgba(255,255,255,0.07)' : 'var(--bnb-gold)', border: 'none', color: modalHoleIdx === total - 1 ? 'rgba(255,255,255,0.2)' : '#fff', borderRadius: 8, padding: '0.5rem', fontWeight: 700, fontSize: '0.82rem', cursor: modalHoleIdx === total - 1 ? 'not-allowed' : 'pointer' }}>
                                        Next →
                                    </button>
                                </div>
                                <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)' }}>{modalHoleIdx + 1} of {total}</div>

                            </div>

                            {/* Right: hole image */}
                            <div style={{ flex: 1, background: '#000', position: 'relative', overflow: 'hidden' }}>
                                {h.image_url ? (
                                    <img src={h.image_url} alt={`Hole ${h.number}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0d2a5e 0%, #040d1f 100%)' }}>
                                        <i className="fa-solid fa-golf-ball-tee" style={{ fontSize: '4rem', color: 'rgba(200,169,81,0.2)', marginBottom: '1rem' }} />
                                        <div style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.85rem' }}>No image available</div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                </div>
            )
        })()}

        </>
    )
}

export default TournamentDetailScreen
